import { useState } from "react";
import { HelpCircle, Mail, Send, ChevronDown, ChevronUp, Shield, Users, MapPin, Bell, AlertTriangle } from "lucide-react";

export default function Support() {
  const [openFaq, setOpenFaq] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    issueType: "",
    message: ""
  });
  const [submitted, setSubmitted] = useState(false);

  const faqs = [
    {
      question: "Come funziona la scansione nelle vicinanze?",
      answer: "Premi il pulsante centrale per cercare utenti entro 50 metri da te. L'app utilizza la posizione GPS per trovare altri utenti Nerbys nelle vicinanze. Riceverai un feedback sonoro e una vibrazione quando vengono trovati nuovi profili."
    },
    {
      question: "Perché devo collegare almeno un social media?",
      answer: "Nerbys è progettato per facilitare connessioni autentiche. Collegando almeno un account social (Instagram, Facebook, TikTok, X o LinkedIn) permetti agli altri utenti di conoscerti meglio e viceversa."
    },
    {
      question: "Come funzionano i profili Pubblici e Privati?",
      answer: "Con un Profilo Pubblico i tuoi social media sono visibili immediatamente. Con un Profilo Privato gli altri utenti devono inviarti una richiesta di connessione prima di vedere i tuoi link social."
    },
    {
      question: "Come viene verificata la mia foto profilo?",
      answer: "Utilizziamo un sistema di verifica AI che controlla l'autenticità della foto profilo. Questo aiuta a prevenire profili falsi e garantisce un ambiente più sicuro per tutti."
    },
    {
      question: "Come posso segnalare un utente?",
      answer: "Vai sul profilo dell'utente e tocca 'Segnala'. Seleziona il motivo della segnalazione e fornisci dettagli se necessario. Il nostro team esaminerà la segnalazione entro 24-48 ore."
    },
    {
      question: "Quanto dura lo stato 'In questo momento'?",
      answer: "Lo stato temporaneo 'In questo momento' scade automaticamente dopo 4 ore. Puoi aggiornarlo o rimuoverlo manualmente in qualsiasi momento."
    },
    {
      question: "Come posso cancellare il mio account?",
      answer: "Vai su Impostazioni > Account > Cancella Account. La cancellazione è permanente e comporta la rimozione di tutti i tuoi dati personali."
    },
    {
      question: "L'app funziona senza connessione internet?",
      answer: "No, Nerbys richiede una connessione internet attiva per funzionare, in quanto la scansione e le connessioni avvengono in tempo reale."
    }
  ];

  const issueTypes = [
    { value: "bug", label: "Segnalazione Bug" },
    { value: "account", label: "Problemi Account" },
    { value: "safety", label: "Sicurezza / Segnalazione Utente" },
    { value: "privacy", label: "Privacy / Dati" },
    { value: "feedback", label: "Feedback Generale" },
    { value: "other", label: "Altro" }
  ];

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log("Form submitted:", formData);
    setSubmitted(true);
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <main data-testid="support-page" className="pt-24 md:pt-32 pb-20">
      <div className="max-w-screen-lg mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="mb-12 text-center">
          <div className="inline-flex items-center justify-center gap-3 mb-6">
            <div className="w-12 h-12 bg-gradient-to-br from-purple-600 to-purple-400 rounded-xl flex items-center justify-center shadow-lg shadow-purple-500/30">
              <HelpCircle className="w-6 h-6 text-white" />
            </div>
          </div>
          <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight mb-4">
            Centro Assistenza
          </h1>
          <p className="text-purple-200/50 text-lg max-w-2xl mx-auto">
            Siamo qui per aiutarti. Trova risposte alle domande comuni o contattaci direttamente.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 md:gap-12">
          {/* FAQ Section */}
          <div>
            <h2 className="text-2xl font-bold text-white mb-6">
              Domande Frequenti
            </h2>
            <div className="space-y-3">
              {faqs.map((faq, index) => (
                <div
                  key={index}
                  data-testid={`faq-item-${index}`}
                  className="bg-purple-950/30 border border-purple-500/10 rounded-xl overflow-hidden"
                >
                  <button
                    onClick={() => setOpenFaq(openFaq === index ? null : index)}
                    className="w-full flex items-center justify-between p-5 text-left hover:bg-purple-500/5 transition-colors"
                  >
                    <span className="font-medium text-white pr-4 text-sm">{faq.question}</span>
                    {openFaq === index ? (
                      <ChevronUp className="w-5 h-5 text-purple-400 flex-shrink-0" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-purple-400 flex-shrink-0" />
                    )}
                  </button>
                  {openFaq === index && (
                    <div className="px-5 pb-5">
                      <p className="text-purple-200/60 text-sm leading-relaxed">
                        {faq.answer}
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Contact Form */}
          <div>
            <h2 className="text-2xl font-bold text-white mb-6">
              Contattaci
            </h2>
            
            {submitted ? (
              <div 
                data-testid="support-form-success"
                className="bg-green-500/10 border border-green-500/30 rounded-2xl p-8 text-center"
              >
                <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-emerald-400 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg shadow-green-500/30">
                  <Send className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">Messaggio Inviato!</h3>
                <p className="text-purple-200/60">
                  Ti risponderemo entro 24-48 ore lavorative.
                </p>
                <button
                  onClick={() => {
                    setSubmitted(false);
                    setFormData({ name: "", email: "", issueType: "", message: "" });
                  }}
                  className="mt-6 text-purple-400 hover:text-purple-300 text-sm font-medium"
                >
                  Invia un altro messaggio
                </button>
              </div>
            ) : (
              <form 
                onSubmit={handleSubmit}
                data-testid="support-form"
                className="bg-purple-950/30 border border-purple-500/10 rounded-2xl p-6 md:p-8 space-y-5"
              >
                {/* Name */}
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-purple-200/70 mb-2">
                    Nome *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    value={formData.name}
                    onChange={handleInputChange}
                    data-testid="support-form-name"
                    className="w-full bg-purple-950/50 border border-purple-500/20 text-white rounded-xl px-4 py-3 focus:ring-2 focus:ring-purple-500 focus:border-transparent placeholder:text-purple-200/30 transition-all"
                    placeholder="Il tuo nome"
                  />
                </div>

                {/* Email */}
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-purple-200/70 mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleInputChange}
                    data-testid="support-form-email"
                    className="w-full bg-purple-950/50 border border-purple-500/20 text-white rounded-xl px-4 py-3 focus:ring-2 focus:ring-purple-500 focus:border-transparent placeholder:text-purple-200/30 transition-all"
                    placeholder="la-tua@email.com"
                  />
                </div>

                {/* Issue Type */}
                <div>
                  <label htmlFor="issueType" className="block text-sm font-medium text-purple-200/70 mb-2">
                    Tipo di Problema *
                  </label>
                  <select
                    id="issueType"
                    name="issueType"
                    required
                    value={formData.issueType}
                    onChange={handleInputChange}
                    data-testid="support-form-issue-type"
                    className="w-full bg-purple-950/50 border border-purple-500/20 text-white rounded-xl px-4 py-3 focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                  >
                    <option value="">Seleziona un'opzione</option>
                    {issueTypes.map((type) => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Message */}
                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-purple-200/70 mb-2">
                    Messaggio *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    rows={5}
                    value={formData.message}
                    onChange={handleInputChange}
                    data-testid="support-form-message"
                    className="w-full bg-purple-950/50 border border-purple-500/20 text-white rounded-xl px-4 py-3 focus:ring-2 focus:ring-purple-500 focus:border-transparent placeholder:text-purple-200/30 transition-all resize-none"
                    placeholder="Descrivi il problema o la tua richiesta..."
                  />
                </div>

                {/* Submit */}
                <button
                  type="submit"
                  data-testid="support-form-submit"
                  className="w-full bg-gradient-to-r from-purple-600 to-purple-500 text-white px-6 py-4 rounded-xl font-bold hover:shadow-lg hover:shadow-purple-500/30 transition-all hover:scale-[1.02] active:scale-[0.98] flex items-center justify-center gap-2"
                >
                  <Send className="w-5 h-5" />
                  Invia Messaggio
                </button>
              </form>
            )}

            {/* Direct Contact */}
            <div className="mt-8 p-6 bg-purple-950/30 border border-purple-500/10 rounded-2xl">
              <h3 className="font-bold text-white mb-4">Contatto Diretto</h3>
              <div className="space-y-3">
                <a
                  href="mailto:support@nerbys.app"
                  data-testid="support-email-link"
                  className="flex items-center gap-3 text-purple-200/60 hover:text-purple-300 transition-colors"
                >
                  <Mail className="w-5 h-5 text-purple-400" />
                  <span>support@nerbys.app</span>
                </a>
                <a
                  href="mailto:privacy@nerbys.app"
                  className="flex items-center gap-3 text-purple-200/60 hover:text-purple-300 transition-colors"
                >
                  <Shield className="w-5 h-5 text-purple-400" />
                  <span>privacy@nerbys.app</span>
                </a>
                <p className="text-xs text-purple-200/40 mt-4">
                  Tempo di risposta medio: 24-48 ore lavorative
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Safety Tips */}
        <div className="mt-16 pt-16 border-t border-purple-500/10">
          <h2 className="text-2xl font-bold text-white mb-8 text-center">
            Consigli per la Sicurezza
          </h2>
          <div className="grid md:grid-cols-4 gap-6">
            {[
              { icon: Shield, title: "Verifica il profilo", desc: "Cerca il badge di verifica AI sui profili" },
              { icon: MapPin, title: "Incontra in pubblico", desc: "Per i primi incontri scegli luoghi affollati" },
              { icon: Users, title: "Segnala comportamenti", desc: "Usa il sistema di segnalazione per utenti sospetti" },
              { icon: AlertTriangle, title: "Proteggi i tuoi dati", desc: "Non condividere informazioni sensibili" },
            ].map((tip, index) => (
              <div
                key={index}
                data-testid={`safety-tip-${index}`}
                className="bg-purple-950/30 border border-purple-500/10 rounded-xl p-6 text-center hover:border-purple-500/30 transition-colors"
              >
                <div className="w-12 h-12 bg-purple-500/10 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <tip.icon className="w-6 h-6 text-purple-400" />
                </div>
                <h3 className="font-bold text-white mb-2 text-sm">{tip.title}</h3>
                <p className="text-purple-200/50 text-xs">{tip.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </main>
  );
}
